import { Helmet } from 'react-helmet-async'
import { filter } from 'lodash'
import { sentenceCase } from 'change-case'
import { useEffect, useState } from 'react'
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Button,
  Popover,
  Checkbox,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Skeleton,
} from '@mui/material'
// components
import Label from '../components/label'
import Iconify from '../components/iconify'
import Scrollbar from '../components/scrollbar'
// sections
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user'
// mock
// import USERLIST from '../_mock/user'
import CustomModal from 'src/components/CustomModal/CustomModal'
import CreateUserForm from 'src/sections/@dashboard/user/CreateUserForm'
import { useUsersStore } from 'src/store/users'
import { useFetchActiveUser, useFetchDeactivateUser } from 'src/services/users'
import { format } from 'date-fns'
import EditUserForm from 'src/sections/@dashboard/user/EditUserForm'

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'email', label: 'Email', alignRight: false },
  { id: 'role', label: 'Role', alignRight: false },
  { id: 'first_name', label: 'First Name', alignRight: false },
  { id: 'last_name', label: 'Last Name', alignRight: false },
  { id: 'company', label: 'Company', alignRight: false },
  { id: 'last_access', label: 'Last Login', alignRight: false },
  { id: 'last_access_token', label: 'Last Access', alignRight: false },
  { id: 'status', label: 'Status', alignRight: false },
  { id: '' },
]

// ----------------------------------------------------------------------

const APP_BAR_DESKTOP = 92

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1
  }
  if (b[orderBy] > a[orderBy]) {
    return 1
  }
  return 0
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index])
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0])
    if (order !== 0) return order
    return a[1] - b[1]
  })
  if (query) {
    return filter(array, (_user) => _user.email.toLowerCase().indexOf(query.toLowerCase()) !== -1)
  }
  return stabilizedThis.map((el) => el[0])
}

export default function UserPage() {
  const [openActions, setOpenActions] = useState(null)
  const [openCreateUser, setOpenCreateUser] = useState(false)
  const [openEditUser, setOpenEditUser] = useState(false)
  const [page, setPage] = useState(0)
  const [order, setOrder] = useState('asc')
  const [selected, setSelected] = useState([])
  const [orderBy, setOrderBy] = useState('email')
  const [filterName, setFilterName] = useState('')
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const fetchDeactivateUser = useFetchDeactivateUser()
  const fetchActivateUser = useFetchActiveUser()
  const { handleUsers, users, isLoading } = useUsersStore((state) => state)
  const [idUserMenu, setIdUserMenu] = useState({ id: null, isActive: null })

  const handleOpenMenu = (event, idUser, isActive) => {
    setOpenActions(event.currentTarget)
    setIdUserMenu({ id: idUser, isActive })
  }

  const handleOpenCreateUser = () => {
    setOpenCreateUser(!openCreateUser)
  }

  const handleCloseCreateUser = () => {
    setOpenCreateUser(!openCreateUser)
  }

  const handleCloseMenu = () => {
    setOpenActions(null)
    setIdUserMenu({ id: null, isActive: null })
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = users.map((n) => n.email)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  const handleDeactivateUser = async () => {
    if (!idUserMenu.id) return

    await fetchDeactivateUser.mutateAsync(idUserMenu.id)
  }

  const handleActivateUser = async () => {
    if (!idUserMenu.id) return

    await fetchActivateUser.mutateAsync(idUserMenu.id)
  }

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name)
    let newSelected = []
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1))
    }
    setSelected(newSelected)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    setPage(0)
    setRowsPerPage(parseInt(event.target.value, 10))
  }

  const handleFilterByName = (event) => {
    setPage(0)
    setFilterName(event.target.value)
  }

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - users.length) : 0

  const filteredUsers = applySortFilter(users, getComparator(order, orderBy), filterName)

  const isNotFound = !filteredUsers.length && !!filterName

  useEffect(() => {
    if (fetchDeactivateUser.isSuccess) {
      const userDehactivate = users.map((user) =>
        user.id === idUserMenu.id ? { ...user, isActive: !idUserMenu.isActive } : user
      )
      handleUsers(userDehactivate)
      setIdUserMenu({ id: null, isActive: null })
    }
  }, [fetchDeactivateUser.isSuccess, fetchDeactivateUser.isError, fetchDeactivateUser.isRefetching])

  useEffect(() => {
    if (fetchActivateUser.isSuccess) {
      const userActivate = users.map((user) =>
        user.id === idUserMenu.id ? { ...user, isActive: !idUserMenu.isActive } : user
      )
      handleUsers(userActivate)
      setIdUserMenu({ id: null, isActive: null })
    }
  }, [fetchActivateUser.isSuccess, fetchActivateUser.isError, fetchActivateUser.isRefetching])

  return (
    <>
      <Helmet>
        <title> User | FDC Dash Admin </title>
      </Helmet>

      <Container sx={{ paddingTop: (APP_BAR_DESKTOP + 24) / 8 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            User
          </Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={handleOpenCreateUser}>
            New User
          </Button>
        </Stack>

        <Card>
          <UserListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            placeholder='"Search user..."'
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={users.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {isLoading
                    ? [...Array(rowsPerPage)].map((_, index) => (
                        <TableRow key={`skeleton-${index}`}>
                          <TableCell padding="checkbox">
                            <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: 0.5 }} />
                          </TableCell>
                          <TableCell component="th" scope="row" padding="none">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Skeleton variant="circular" width={40} height={40} />
                              <Skeleton variant="text" width={180} />
                            </Stack>
                          </TableCell>
                          <TableCell><Skeleton variant="text" width={80} /></TableCell>
                          <TableCell><Skeleton variant="text" width={90} /></TableCell>
                          <TableCell><Skeleton variant="text" width={90} /></TableCell>
                          <TableCell><Skeleton variant="text" width={100} /></TableCell>
                          <TableCell><Skeleton variant="text" width={110} /></TableCell>
                          <TableCell><Skeleton variant="text" width={110} /></TableCell>
                          <TableCell><Skeleton variant="rounded" width={60} height={24} /></TableCell>
                          <TableCell align="right">
                            <Skeleton variant="circular" width={28} height={28} />
                          </TableCell>
                        </TableRow>
                      ))
                    : filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const {
                      id,
                      email,
                      groups,
                      isActive,
                      companyClient,
                      firstName,
                      lastName,
                      lastAccess,
                      lastAccessToken,
                    } = row

                    const selectedUser = selected.indexOf(email) !== -1

                    return (
                      <TableRow hover key={id} tabIndex={-1} role="checkbox" selected={selectedUser}>
                        <TableCell padding="checkbox">
                          <Checkbox checked={selectedUser} onChange={(event) => handleClick(event, email)} />
                        </TableCell>

                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar alt={email} src={''} />
                            <Typography variant="subtitle2" noWrap>
                              {email}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="left">{groups}</TableCell>
                        <TableCell align="left">{firstName}</TableCell>
                        <TableCell align="left">{lastName}</TableCell>
                        <TableCell align="left">{companyClient?.name}</TableCell>
                        <TableCell align="left">
                          {lastAccessToken ? format(new Date(lastAccessToken), 'dd/MM/yyyy:HH:mm') : ''}
                        </TableCell>
                        <TableCell align="left">
                          {lastAccess ? format(new Date(lastAccess), 'dd/MM/yyyy:HH:mm') : ''}
                        </TableCell>
                        <TableCell align="left">
                          <Label color={(!isActive && 'error') || 'success'}>
                            {sentenceCase(isActive ? 'Active' : 'Inactive')}
                          </Label>
                        </TableCell>

                        <TableCell align="right">
                          <IconButton size="large" color="inherit" onClick={(e) => handleOpenMenu(e, id, isActive)}>
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                  {!isLoading && emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={10} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Not found
                          </Typography>

                          <Typography variant="body2">
                            No results found for &nbsp;
                            <strong>&quot;{filterName}&quot;</strong>.
                            <br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={users.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(openActions)}
        anchorEl={openActions}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 140,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem onClick={() => setOpenEditUser(!openEditUser)}>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Edit
        </MenuItem>

        <MenuItem
          sx={{ color: idUserMenu.isActive ? 'error.main' : 'success.main' }}
          onClick={() => (idUserMenu.isActive ? handleDeactivateUser() : handleActivateUser())}
        >
          <Iconify icon={idUserMenu.isActive ? 'eva:trash-2-outline' : 'mdi:check-bold'} sx={{ mr: 2 }} />
          {idUserMenu.isActive ? 'Deactivate' : 'Activate'}
        </MenuItem>
      </Popover>
      <CustomModal
        open={openCreateUser}
        handleClose={handleCloseCreateUser}
        borderRadius={1}
        width={400}
        padding="1.5rem 1.5rem 5rem 1.5rem"
      >
        <CreateUserForm />
      </CustomModal>
      <CustomModal
        open={openEditUser}
        handleClose={() => setOpenEditUser(!openEditUser)}
        borderRadius={1}
        width={400}
        padding="1.5rem 1.5rem 5rem 1.5rem"
      >
        <EditUserForm userId={idUserMenu} />
      </CustomModal>
    </>
  )
}
